<script lang="ts">
  import { tick, onMount, onDestroy } from 'svelte';
  import Portal from 'svelte-portal';
  import hotkeys from 'hotkeys-js';
  import focusLock from 'dom-focus-lock';
  import { clickOutside } from 'svelte-use-click-outside';
  import { browser } from '$app/environment';
  import { modals } from '$lib/stores/modals';
  import { getFocusableIn } from 'focus-lock';

  export let modalKey: string;
  export let size: 'sm' | 'md' | 'fullscreen' = 'md';
  export let openHotKeys: string[] = [];
  export let autoCloseAt: number | null = null;
  export let ariaLabel: string;
  export let withBackdrop = true;

  let isOpen: boolean;
  let contentDiv: HTMLDivElement;

  // a modal can cycle through its focusable content via keyboard mechanisms
  // but must have keyboard mechanisms to escape as well (via esc key, close button, etc)
  // more on content traps: https://www.w3.org/TR/WCAG20-TECHS/G21
  const handler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleClose();
    }

    if (e.key === 'Tab' && !e.shiftKey) {
      const focusables = getFocusableIn(contentDiv);

      if (focusables && focusables[focusables.length - 1].node == e.target) {
        e.preventDefault();
        focusables[0].node.focus();
      }
    }
  };

  $: {
    isOpen = $modals.has(modalKey);

    if (browser && isOpen) {
      onOpen();
    }
  }

  async function onOpen() {
    // Await one tick to ensure modal content is rendered
    await tick();

    focusLock.on(contentDiv);
    fullScreenLockScrolling();
  }

  const handleOpen = () => {
    if (!isOpen) {
      modals.open(modalKey);
    }
  };

  const handleClose = () => {
    if (isOpen) {
      const previousElement = modals.close(modalKey);
      focusLock.off(contentDiv);
      fullScreenUnlockScrolling();

      if (previousElement) {
        previousElement.focus();
      }
    }
  };

  const autoCloseIfNeeded = () => {
    if (isOpen && autoCloseAt && window.innerWidth > autoCloseAt) {
      modals.close(modalKey);
    }
  };

  const fullScreenUnlockScrolling = () => {
    if (size == 'fullscreen') {
      const scrollY = document.body.style.top;

      document.body.style.top = '';
      document.body.style.position = '';
      document.body.style.width = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    }
  };

  const fullScreenLockScrolling = () => {
    if (size === 'fullscreen') {
      document.body.style.top = `-${window.scrollY}px`;
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
  };

  const handleResize = () => {
    autoCloseIfNeeded();
  };

  const configureHotKeys = () => {
    openHotKeys.forEach((key) => hotkeys(key, 'modals', handleOpen));
    hotkeys.setScope('modals');
  };

  onMount(() => {
    configureHotKeys();
    handleResize();
  });

  onDestroy(() => {
    hotkeys.deleteScope('modals');
  });
</script>

<svelte:window on:resize={handleResize} />

<Portal target="#modals">
  <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
  <div
    bind:this={contentDiv}
    on:keydown={handler}
    on:pointermove|preventDefault
    use:clickOutside={handleClose}
    class={`modal size-${size}`}
    class:hidden={!isOpen}
    role="dialog"
    aria-modal="true"
    aria-label={ariaLabel}
  >
    <slot onCloseClicked={handleClose} />
  </div>

  {#if withBackdrop}
    <div class="backdrop" class:hidden={!isOpen} />
  {/if}
</Portal>

<style lang="scss">
  @import 'src/scss/variables';

  .modal {
    position: fixed;
    width: 100%;
    left: 50%;
    transform: translateX(-50%);
    z-index: map-get($zIndex, modal);

    &.size-sm {
      max-width: 634px;
      padding: 0 24px;
      top: 50%;
      transform: translate(-50%, -50%);
    }

    &.size-md {
      max-width: 1190px;
      height: calc(100vh - 208px);
      top: 104px;
    }

    &.size-fullscreen {
      width: 100vw;
      height: 100%;
      transform: none;
      top: 0;
      left: 0;
    }
  }

  .backdrop {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background: black;
    opacity: 0.4;
    z-index: map-get($zIndex, modal-backdrop);
  }

  .hidden {
    display: none;
  }
</style>
